import { Grid, TextField, Theme } from '@mui/material';
import { RHFAutocomplete } from 'src/components/minimals/form';
import DateAggregationSelect from '../sidebar/form-inputs/DateAggregationSelect';
import { StyledFilterButton } from '../asset-report-sidebar/style';
import {
  ASSET_AGGREGATION_OPTIONS,
  NO_DATE_LIMIT_AGGREGATION_INTERVALS,
} from '../../config/config';
import { FilterIcon } from 'src/assets/icons/FilterIcon';
import { ITimezoneOption } from 'react-timezone-select';
import { FieldValues, UseFormSetValue, UseFormWatch } from 'react-hook-form';
import { useEffect, useState } from 'react';
import { timeFrameOptions } from '../../utils/timeFrameToDateUtils';
import { TimeFrameRangePicker } from '../sidebar/form-inputs/TimeFrameRangePicker';
import TimeFrameDropdown from '../sidebar/form-inputs/TimeFrameDropdown';
import dayjs from 'dayjs';

type AssetReportBaseLevelFormItemsProps = {
  testId: string;
  theme: Theme;
  options: ITimezoneOption[];
  isFilterBtnActive: boolean;
  toggleSidebar: (state: boolean) => void;
  watch: UseFormWatch<FieldValues>;
  setValue: UseFormSetValue<FieldValues>;
  isMobile: boolean;
  setError: (error: boolean) => void;
  disabled?: boolean;
};
export const AssetReportBaseLevelFormItems = ({
  testId,
  theme,
  options,
  isFilterBtnActive,
  toggleSidebar,
  watch,
  setValue,
  isMobile,
  setError,
  disabled,
}: AssetReportBaseLevelFormItemsProps) => {
  const [customSelected, setIsCustomSelected] = useState<boolean>(false);
  const [showTimeFrameError, setShowTimeFrameError] = useState<boolean>(false);

  useEffect(() => {
    setIsCustomSelected(typeof watch('data.timeFrame') !== 'string');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watch('data.timeFrame')]);

  const gridSize = 4;

  const smallGridSize = gridSize;
  const mediumGridSize = gridSize;
  const largeGridSize = gridSize;
  const extraLargeGridSize = 2;

  const isOverSixMonths = (timeFrame: any): boolean => {
    if (!Array.isArray(timeFrame) || timeFrame.length !== 2) return false;
    const [start, end] = timeFrame;
    return dayjs(end).diff(dayjs(start), 'month', true) > 6;
  };

  return (
    <>
      <Grid item xs={12} sm={smallGridSize} md={mediumGridSize} xl={extraLargeGridSize}>
        <TextField
          value="Asset"
          disabled={true}
          size="small"
          sx={{ width: '100%' }}
          label="Report Type"
        />
      </Grid>
      <Grid
        item
        xs={12}
        sm={smallGridSize}
        md={mediumGridSize}
        lg={largeGridSize}
        xl={extraLargeGridSize}
      >
        <RHFAutocomplete
          name="data.timeZone"
          isOptionEqualToValue={(option, value) => option.value === value.value}
          rules={{ required: 'The field is required' }}
          options={options}
          disableClearable
          disabled={disabled}
          value={watch('data.timeZone.value')}
          size="small"
          renderInput={(params) => (
            <TextField
              {...params}
              inputProps={{
                ...params.inputProps,
                'data-sm': testId,
              }}
              label={'Time Zone *'}
            />
          )}
        />
      </Grid>

      <Grid
        item
        xs={12}
        sm={smallGridSize}
        md={mediumGridSize}
        lg={largeGridSize}
        xl={extraLargeGridSize}
      >
        <DateAggregationSelect
          testId="asset-report-aggregation-interval"
          disabled={disabled}
          value={watch('data.aggregationInterval')}
          onChange={(event) => {
            const newVal = event.target!.value as string;
            const timeFrame = watch('data.timeFrame');

            const isShortInterval = !NO_DATE_LIMIT_AGGREGATION_INTERVALS.includes(newVal);

            const isInvalid = isShortInterval && isOverSixMonths(timeFrame);

            setShowTimeFrameError(isInvalid);
            setError(isInvalid);
            setValue('data.aggregationInterval', newVal);
          }}
          timeFrames={ASSET_AGGREGATION_OPTIONS}
        />
      </Grid>

      <Grid
        item
        xs={12}
        sm={smallGridSize}
        md={mediumGridSize}
        lg={largeGridSize}
        xl={extraLargeGridSize}
      >
        <TimeFrameDropdown
          disabled={disabled}
          value={watch('data.timeFrame')}
          onChange={(val: any) => {
            const agg = watch('data.aggregationInterval');
            const isShortInterval = !NO_DATE_LIMIT_AGGREGATION_INTERVALS.includes(agg);
            const isInvalid = isShortInterval && isOverSixMonths(val);

            setShowTimeFrameError(isInvalid);
            setError(isInvalid);
            setValue('data.timeFrame', val);
          }}
          testId="asset-report-time-frame"
          timeFrames={timeFrameOptions}
          error={
            showTimeFrameError
              ? 'For aggregation intervals under 30 minutes the report time frame cannot be more than 6 months.'
              : undefined
          }
        />
      </Grid>
      {Array.isArray(watch('data.timeFrame')) && (
        <Grid
          item
          xs={12}
          sm={customSelected ? 6 : smallGridSize}
          md={customSelected ? 6 : mediumGridSize}
          lg={largeGridSize}
          xl={extraLargeGridSize}
        >
          <TimeFrameRangePicker
            disabled={disabled}
            value={watch('data.timeFrame')}
            isMobile={isMobile}
            onChange={(val) => {
              const agg = watch('data.aggregationInterval');
              const isShortInterval = !NO_DATE_LIMIT_AGGREGATION_INTERVALS.includes(agg);
              const isInvalid = isShortInterval && isOverSixMonths(val);

              setShowTimeFrameError(isInvalid);
              setError(isInvalid);
              setValue('data.timeFrame', val);
            }}
          />
        </Grid>
      )}
      <Grid item sm="auto">
        <StyledFilterButton
          onClick={() => toggleSidebar(true)}
          endIcon={<FilterIcon />}
          variant="text"
          size="small"
          data-sm="filter-btn"
          isActive={isFilterBtnActive}
          theme={theme}
          disabled={disabled}
        >
          Filter
        </StyledFilterButton>
      </Grid>
    </>
  );
};
